// @myroute/shared — types, validators, and helpers shared between web + mobile + edge fns.
//
// Web today re-exports these from `apps/web/src/services/leads.ts` so the
// existing call sites keep working unchanged. The Phase 5 mobile app will
// import from this package directly so the two clients can't drift.

export const SHARED_VERSION = "0.1.0";

// ----------------------------------------------------------------------------
// Lead status — DB enum from migration 0001 (do not rename without a migration)
// ----------------------------------------------------------------------------
export const LEAD_STATUSES = [
  "new",
  "no_answer",
  "scheduled",
  "follow_up",
  "closed",
  "do_not_solicit",
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/**
 * User-facing label for each `LeadStatus`. "closed" maps to "Sold" because in
 * this product the only path to `lead.status = 'closed'` is the Sold outcome
 * button on a stop, which represents a closed-won sale in life-insurance
 * terms. The DB enum stays `closed` (rename would need a migration).
 */
export const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: "New",
  no_answer: "No answer",
  scheduled: "Scheduled",
  follow_up: "Follow-up",
  closed: "Sold",
  do_not_solicit: "DNC",
};

// ----------------------------------------------------------------------------
// Route + stop status enums (from 0001)
// ----------------------------------------------------------------------------
export const ROUTE_STATUSES = [
  "draft",
  "published",
  "in_progress",
  "completed",
  "cancelled",
] as const;
export type RouteStatus = (typeof ROUTE_STATUSES)[number];

export const ROUTE_STOP_STATUSES = [
  "pending",
  "arrived",
  "completed",
  "skipped",
  "no_answer",
] as const;
export type RouteStopStatus = (typeof ROUTE_STOP_STATUSES)[number];

// ----------------------------------------------------------------------------
// Lead shape — the canonical fields read by every client + edge function
// ----------------------------------------------------------------------------
export interface Lead {
  id: string;
  team_id: string;
  import_id?: string | null;

  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  dob: string | null;
  email: string | null;
  phone: string | null;
  phone_alt: string | null;

  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_combined: string | null;

  lat: number | null;
  lng: number | null;
  geocoded_at: string | null;
  geocode_accuracy: string | null;

  source: string | null;
  status: LeadStatus;
  status_changed_at: string;
  assigned_to: string | null;
  notes: string | null;

  /** Everything the source CSV had that didn't map to a canonical column —
   *  policy count, household premium, custom carrier fields, etc. */
  custom_fields: Record<string, unknown>;

  created_at: string;
  updated_at: string;
}
